import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/contexts/SettingsContext";

const LS_KEY = "cherish_lead_shown_v1";
const OCCASIONS = ["Birthday", "Anniversary", "Wedding", "Corporate", "Memorial", "Just Because"];

const LeadCapturePopup = () => {
    const { settings } = useSettings();
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", occasion: "Birthday" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!settings) return;
        const enabled = settings.homepage?.lead_popup_enabled !== false;
        if (!enabled) return;
        const already = localStorage.getItem(LS_KEY);
        if (already) return;
        // Only show on public shop/home routes — not on dashboard/admin/team/checkout
        const path = typeof window !== "undefined" ? window.location.pathname : "/";
        if (path.startsWith("/dashboard") || path.startsWith("/admin") || path.startsWith("/team") || path.startsWith("/checkout")) return;
        const t = setTimeout(() => setOpen(true), 4500);
        return () => clearTimeout(t);
    }, [settings]);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/leads", { ...form, source: "popup" });
            localStorage.setItem(LS_KEY, JSON.stringify({ at: new Date().toISOString() }));
            toast.success("Thanks! We'll be in touch soon 💌");
            setOpen(false);
        } catch (e) {
            toast.error("Something went wrong — please try again.");
        } finally {
            setSaving(false);
        }
    };

    const close = () => {
        localStorage.setItem(LS_KEY, JSON.stringify({ dismissed: true }));
        setOpen(false);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
            <DialogContent className="max-w-md rounded-3xl border-0 glass-strong p-0 overflow-hidden" data-testid="lead-popup">
                <div className="relative">
                    <div className="ambient-orb bg-brand-peach -top-16 -left-16 w-64 h-64" />
                    <div className="ambient-orb bg-brand-lavender -bottom-16 -right-16 w-64 h-64" />
                </div>
                <DialogHeader className="text-center pt-8 px-6 relative z-10">
                    <div className="mx-auto w-14 h-14 rounded-full bg-gradient-to-br from-primary to-brand-gold flex items-center justify-center mb-3">
                        <Sparkles className="w-7 h-7 text-white" />
                    </div>
                    <DialogTitle className="font-display text-2xl md:text-3xl tracking-tight">Craft a miniature to remember</DialogTitle>
                    <DialogDescription className="text-sm">
                        Tell us about the occasion and we'll help you design the perfect keepsake.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="p-6 space-y-3 relative z-10">
                    <div className="grid grid-cols-2 gap-3">
                        <Input required data-testid="lead-name" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl h-11 bg-white/50" />
                        <Input required data-testid="lead-phone" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl h-11 bg-white/50" />
                    </div>
                    <Input required type="email" data-testid="lead-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl h-11 bg-white/50" />
                    <div className="grid grid-cols-2 gap-3">
                        <Input data-testid="lead-city" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-xl h-11 bg-white/50" />
                        <select
                            data-testid="lead-occasion"
                            value={form.occasion}
                            onChange={(e) => setForm({ ...form, occasion: e.target.value })}
                            className="rounded-xl h-11 bg-white/50 border border-input px-3 text-sm"
                        >
                            {OCCASIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                    <Button type="submit" disabled={saving} data-testid="lead-submit" className="w-full rounded-full h-11 bg-primary hover:bg-primary/90">
                        {saving ? "Sending…" : "Get in touch"}
                    </Button>
                    <button type="button" onClick={close} data-testid="lead-skip" className="w-full text-xs text-muted-foreground hover:underline pt-1">
                        No thanks, I'll browse first
                    </button>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default LeadCapturePopup;
